// BUILD-042 October 8 21:19 addendum root-cause fix — "ensure every offered download is
// readable or explicitly described unavailable/invalid local fixture, never silently replace
// historical bytes."
//
// This is a LIGHTWEIGHT STRUCTURAL SANITY CHECK, not a PDF parser/renderer/signing engine. It
// inspects the first bytes of an already-fetched file for the handful of literal byte sequences
// every real PDF must contain (the %PDF- header, and at least one of the structural keywords
// every valid PDF's trailer/xref mechanism requires), the same category of check already used
// elsewhere in this codebase for file-type gating — not a new rendering or signing feature. It
// deliberately does NOT guarantee a file opens cleanly (only a real parser/renderer can prove
// that — see the correction-cycle verification notes in local-sample-documents.mjs), but it DOES
// reliably catch exactly the class of defect this BUILD has repeatedly produced: a tiny text
// stub that merely starts with the literal text "%PDF-1.4" with no real PDF structure at all
// (the historical invalid fixtures Builder/Owner found). Old invalid bytes are NEVER rewritten
// or deleted by this check — it only decides what the UI tells the user before offering to open
// them.
export interface FileCheckResult {
  looksValid: boolean;
  reason?: string;
}

const MIN_PLAUSIBLE_PDF_BYTES = 300; // every real PDF produced by this app's own fixtures is >1000 bytes; the known-invalid historical stubs are 52-65 bytes.

export function checkPdfBytes(bytes: Uint8Array): FileCheckResult {
  if (bytes.length < MIN_PLAUSIBLE_PDF_BYTES) {
    return { looksValid: false, reason: `This file is only ${bytes.length} bytes — too small to be a real PDF. This is almost certainly an old invalid local test fixture, not an openable document.` };
  }
  const text = new TextDecoder('latin1').decode(bytes.subarray(0, 16));
  if (!text.startsWith('%PDF-')) {
    return { looksValid: false, reason: 'This file does not have a valid PDF header. It is not an openable document.' };
  }
  const fullText = new TextDecoder('latin1').decode(bytes);
  const hasStructure = fullText.includes('endobj') && (fullText.includes('startxref') || fullText.includes('xref'));
  if (!hasStructure) {
    return { looksValid: false, reason: 'This file starts with a PDF header but has no real PDF structure — it is a known-invalid local test fixture, not an openable document.' };
  }
  return { looksValid: true };
}

/**
 * October 10 12:24 addendum — "images actually view... don't force image/HEIC into PDF or
 * claim unsupported browser preview." A document template/submission is allowed to be a PDF
 * OR a JPEG/PNG/HEIC scan (hr-document-storage.ts's own ALLOWED_CONTENT_TYPES). The PREVIOUS
 * version of this check only recognized the PDF signature, so a genuinely valid image upload
 * (e.g. a phone-camera JPEG, or the PNG screenshot test upload seen in this cycle's diagnosis)
 * would be wrongly reported as "could not be opened" — a false negative on real, valid files.
 * This checks each format by its own real magic-byte signature; it still never attempts to
 * render or re-encode anything, same lightweight-sanity-check discipline as before.
 */
function checkImageBytes(bytes: Uint8Array): FileCheckResult {
  if (bytes.length < 16) {
    return { looksValid: false, reason: `This file is only ${bytes.length} bytes — too small to be a real image.` };
  }
  const b = bytes;
  const isPng = b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;
  const isJpeg = b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
  // HEIC/HEIF: ISO base media file format box "ftyp" at offset 4, with a heic/heix/mif1/msf1
  // brand — the standard way to identify it without a full container parser.
  const ftyp = new TextDecoder('latin1').decode(bytes.subarray(4, 8));
  const brand = new TextDecoder('latin1').decode(bytes.subarray(8, 12));
  const isHeic = ftyp === 'ftyp' && ['heic', 'heix', 'mif1', 'msf1', 'heim', 'heis'].includes(brand);
  if (isPng || isJpeg || isHeic) return { looksValid: true };
  return { looksValid: false, reason: 'This file does not have a valid image header (PNG, JPEG, or HEIC). It is not an openable image.' };
}

/** Dispatches to the PDF or image check based on the server's declared contentType. When
 *  contentType is unknown/absent, falls back to trying both — a real file will match exactly
 *  one; a known-invalid stub will match neither. */
export function checkFileBytes(bytes: Uint8Array, contentType?: string): FileCheckResult {
  const ct = (contentType || '').toLowerCase();
  if (ct === 'application/pdf') return checkPdfBytes(bytes);
  if (ct === 'image/jpeg' || ct === 'image/png' || ct === 'image/heic') return checkImageBytes(bytes);
  const pdfResult = checkPdfBytes(bytes);
  if (pdfResult.looksValid) return pdfResult;
  return checkImageBytes(bytes);
}

/** Fetch the URL's bytes and run checkFileBytes against them, without assuming the content-type
 *  the server declared is itself trustworthy for a PDF check (the invalid historical fixtures
 *  were ALSO served with Content-Type: application/pdf) — but DOES use the response's own
 *  Content-Type to pick PDF vs. image checking, since that header is set by this app's own
 *  storage layer from the originally-uploaded contentType, not user-controlled at request time. */
export async function checkDownloadUrl(url: string): Promise<FileCheckResult> {
  const res = await fetch(url);
  if (!res.ok) return { looksValid: false, reason: `Download failed (HTTP ${res.status}).` };
  const buf = new Uint8Array(await res.arrayBuffer());
  return checkFileBytes(buf, res.headers.get('content-type') ?? undefined);
}
